import type { AuthenticationResult } from '@azure/msal-browser';
import { LogLevel, PublicClientApplication } from '@azure/msal-browser';
import type { TokenClaims } from '@azure/msal-common';
import axios from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';
import type { User } from '../Types/interfaces';
import { LoginUser } from './apiHelper';
import { QUERY_KEYS } from '../Constants/queryKeys';
import type { QueryClient } from '@tanstack/react-query';

const appRedirectUri = process.env.REACT_APP_REDIRECT_URI;

export const ubbLoginConfig = {
  auth: {
    clientId: process.env.REACT_APP_UBB_CLIENT_ID ?? '',
    authority: process.env.REACT_APP_UBB_AUTHORITY_URL ?? '',
    redirectUri: appRedirectUri,
    postLogoutRedirectUri: appRedirectUri,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (
        level: LogLevel,
        message: string,
        containsPii: boolean
      ): void => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
    },
  },
};

export const b2cPolicies = {
  names: {
    signUpSignIn: 'B2C_1_UBB_SignUp_SignIn',
    forgotPassword: 'B2C_1_UBB_PasswordReset',
  },
  authorities: {
    signUpSignIn: {
      authority:
        'https://ubbexternal.b2clogin.com/ubbexternal.onmicrosoft.com/B2C_1_UBB_SignUp_SignIn',
    },
    forgotPassword: {
      authority:
        'https://ubbexternal.b2clogin.com/ubbexternal.onmicrosoft.com/B2C_1_UBB_PasswordReset',
    },
  },
  authorityDomain: 'ubbexternal.b2clogin.com',
};

export const externalConfig = {
  auth: {
    clientId: process.env.REACT_APP_EXTERNAL_CLIENT_ID ?? '',
    authority: b2cPolicies.authorities.signUpSignIn.authority,
    knownAuthorities: [b2cPolicies.authorityDomain],
    redirectUri: appRedirectUri,
    navigateToLoginRequestUrl: false,
    postLogoutRedirectUri: appRedirectUri,
    scope: [
      process.env.REACT_APP_EXTERNAL_AUTHORITY_URL ?? '',
      'openid',
      'offline_access',
    ],
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (
        level: LogLevel,
        message: string,
        containsPii: boolean
      ): void => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
    },
  },
};

export declare type IdTokenClaims = TokenClaims & { [key: string]: string };

export const compareIssuingPolicy = (
  idTokenClaims: IdTokenClaims,
  policyToCompare: string
): boolean => {
  const tfpMatches =
    Object.hasOwn(idTokenClaims, 'tfp') &&
    idTokenClaims['tfp']?.toLowerCase() === policyToCompare.toLowerCase();

  const acrMatches =
    Object.hasOwn(idTokenClaims, 'acr') &&
    idTokenClaims['acr']?.toLowerCase() === policyToCompare.toLowerCase();

  return tfpMatches || acrMatches;
};

const formatName = (name: string): { lastName: string; firstName: string } => {
  const parts = name.trim().split(' ');

  if (parts.length === 1) {
    return { lastName: '', firstName: parts[0] };
  }

  const firstName = parts.slice(0, parts.length - 1).join(' ');
  const lastName = parts[parts.length - 1];

  return { lastName, firstName };
};

export const handleUBBLoginResult = async (
  result: AuthenticationResult,
  queryClient: QueryClient
): Promise<void> => {
  const tokenClaims = result.idTokenClaims as IdTokenClaims;

  // Check if every necessary information is present
  if (!result.account || tokenClaims == null) {
    throw new Error('No account information found.');
  }

  // Próbáljunk különböző helyekről név adatokat kinyerni
  let lastName = '';
  let firstName = '';

  // 1. Próbáljuk meg a 'name' mezőből
  if (tokenClaims['name']) {
    const formatted = formatName(tokenClaims['name']);
    lastName = formatted.lastName;
    firstName = formatted.firstName;
  }

  // 2. Ha üres, próbáljuk meg a family_name és given_name mezőkből
  if (!lastName && tokenClaims['family_name']) {
    lastName = tokenClaims['family_name'];
  }
  if (!firstName && tokenClaims['given_name']) {
    firstName = tokenClaims['given_name'];
  }

  // 3. Ha még mindig üres, próbáljuk meg az account objektumból
  if (!lastName && result.account.name) {
    const formatted = formatName(result.account.name);
    lastName = formatted.lastName;
    firstName = formatted.firstName;
  }

  console.log('FINAL - lastName:', lastName, 'firstName:', firstName);

  // Set the auth header globally
  axios.defaults.headers.common = {
    Authorization: `Bearer ${result.idToken}`,
  };

  // Send login request and set user
  const user: User = await LoginUser(
    lastName,
    firstName,
    result.account?.username || ''
  );
  user.accessToken = result.idToken;

  queryClient.setQueryData(QUERY_KEYS.USER, user);
};

export const handleExternalLoginResult = async (
  result: AuthenticationResult,
  queryClient: QueryClient
): Promise<void> => {
  const tokenClaims = result.idTokenClaims as IdTokenClaims;
  // Check if every necessary information is present
  if (!result.account || tokenClaims == null) {
    throw new Error('No account information found.');
  }

  // Set the auth header globally
  axios.defaults.headers.common = {
    Authorization: `Bearer ${result.accessToken}`,
  };

  // Send login request and set user
  const user: User = await LoginUser(
    tokenClaims['family_name'],
    tokenClaims['given_name'],
    result.account?.username || ''
  );
  user.accessToken = result.accessToken;

  queryClient.setQueryData(QUERY_KEYS.USER, user);
};

// Check whether a user is already logged in and if yes set the current user and access token
// For example this can happen if the page was reloaded
export const checkActiveUser = async (
  queryClient: QueryClient
): Promise<void> => {
  try {
    // This will get the active user even if the login happend with the external instance
    const user = ubbLoginInstance.getActiveAccount();
    if (user?.environment !== undefined) {
      const authority = user.environment || '';
      // Check authority to use the proper instance
      if (authority.startsWith('login.windows.net')) {
        await ubbLoginInstance
          .acquireTokenSilent({ scopes: [] })
          .then(async (result: AuthenticationResult) => {
            await handleUBBLoginResult(result, queryClient);
          })
          .catch((e: unknown) => {
            console.error(e);
          });
      } else {
        await externalInstance
          .acquireTokenSilent({ scopes: externalConfig.auth.scope })
          .then(async (result: AuthenticationResult) => {
            await handleExternalLoginResult(result, queryClient);
          })
          .catch((e: unknown) => {
            console.error(e);
          });
      }
    }
  } catch (error) {
    console.error(error);
  }
};

export const ubbLoginInstance = new PublicClientApplication(ubbLoginConfig);
export const externalInstance = new PublicClientApplication(externalConfig);

// ---------------------------------------------------------------------------
// Keeping the login token fresh
//
// The token in the Authorization header is only set at login or page load,
// but it expires after about an hour. Before each request the token is
// renewed silently if it's about to expire, and a request that still gets a
// 401 is retried once with a renewed token.
// ---------------------------------------------------------------------------

const TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000;

type AuthRetryRequestConfig = InternalAxiosRequestConfig & {
  authRetried?: boolean;
};

let pendingTokenRefresh: Promise<string | null> | null = null;

const getCurrentToken = (): string | null => {
  const header = axios.defaults.headers.common?.Authorization;
  return typeof header === 'string' && header.startsWith('Bearer ')
    ? header.slice('Bearer '.length)
    : null;
};

// expiry of a JWT in milliseconds, or null if it can't be read
const getTokenExpiry = (token: string): number | null => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(payload)) as { exp?: number };
    return exp ? exp * 1000 : null;
  } catch {
    return null;
  }
};

// same choice of login instance and token as checkActiveUser
const acquireFreshToken = async (): Promise<string | null> => {
  const account = ubbLoginInstance.getActiveAccount();
  if (account?.environment === undefined) {
    return null;
  }

  if (account.environment.startsWith('login.windows.net')) {
    const result = await ubbLoginInstance.acquireTokenSilent({
      scopes: [],
      forceRefresh: true,
    });
    return result.idToken;
  }

  const result = await externalInstance.acquireTokenSilent({
    scopes: externalConfig.auth.scope,
    forceRefresh: true,
  });
  return result.accessToken;
};

// several requests can need a new token at once: they share one renewal
const refreshAuthToken = (): Promise<string | null> => {
  if (!pendingTokenRefresh) {
    pendingTokenRefresh = acquireFreshToken()
      .then((token) => {
        if (token) {
          axios.defaults.headers.common = {
            Authorization: `Bearer ${token}`,
          };
        }
        return token;
      })
      .catch((error: unknown) => {
        console.error('Could not renew the login token', error);
        return null;
      })
      .finally(() => {
        pendingTokenRefresh = null;
      });
  }
  return pendingTokenRefresh;
};

axios.interceptors.request.use(async (config) => {
  const token = getCurrentToken();
  const expiry = token ? getTokenExpiry(token) : null;

  if (expiry !== null && expiry - Date.now() < TOKEN_REFRESH_MARGIN_MS) {
    const freshToken = await refreshAuthToken();
    if (freshToken) {
      config.headers.set('Authorization', `Bearer ${freshToken}`);
    }
  }
  return config;
});

axios.interceptors.response.use(undefined, async (error: unknown) => {
  if (
    axios.isAxiosError(error) &&
    error.response?.status === 401 &&
    error.config &&
    !(error.config as AuthRetryRequestConfig).authRetried
  ) {
    const config = error.config as AuthRetryRequestConfig;
    config.authRetried = true;

    const freshToken = await refreshAuthToken();
    if (freshToken) {
      config.headers.set('Authorization', `Bearer ${freshToken}`);
      return axios.request(config);
    }
  }
  return Promise.reject(error);
});
